import { Trash2 } from 'lucide-react'
import type { GoalContributionResponse } from '@/api/goals'
import { useFormatCurrency } from '@/hooks/useFormatCurrency'
import { formatMonth, formatShortDate } from '@/lib/formatDate'
import { groupByMonth } from '@/lib/goalPace'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'

/** Contributions grouped by month, newest first, each month with its net. */
export function GoalHistory({
  contributions,
  onDelete,
  deleting,
}: {
  contributions: GoalContributionResponse[]
  /** Omitted when the history can't be changed (read-only, or the goal is done). */
  onDelete?: (id: string) => void
  deleting?: boolean
}) {
  const { formatWhole } = useFormatCurrency()
  const signed = (amount: number) => `${amount < 0 ? '−' : '+'}${formatWhole(Math.abs(amount))}`

  return (
    <div>
      {groupByMonth(contributions).map((group) => (
        <section key={group.month} aria-label={formatMonth(group.month)}>
          <div className="mt-3 flex justify-between text-[11px] font-bold uppercase tracking-[0.07em] text-[var(--fl-text-2)]">
            <span>{formatMonth(group.month)}</span>
            <span className={cn('tabular-nums', group.net > 0 && 'text-[var(--fl-income)]')}>
              {signed(group.net)}
            </span>
          </div>
          <ul className="divide-y divide-[var(--fl-divider)]">
            {group.items.map((c) => (
              <li
                key={c.id}
                className="flex items-center justify-between gap-3 py-2.5 text-[13.5px]"
              >
                <span className="min-w-0">
                  <b
                    className={cn(
                      'font-medium tabular-nums',
                      c.amount < 0 ? 'text-[var(--fl-error)]' : 'text-[var(--fl-text-1)]'
                    )}
                  >
                    {signed(c.amount)}
                  </b>
                  <span className="text-[12.5px] text-[var(--fl-text-2)]">
                    {c.sourceAccountName &&
                      ` · ${c.amount < 0 ? 'back to' : 'from'} ${c.sourceAccountName}`}
                    {` · ${formatShortDate(c.contributedAt)}`}
                    {c.note && ` · ${c.note}`}
                  </span>
                </span>
                {onDelete && (
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    onClick={() => onDelete(c.id)}
                    disabled={deleting}
                    className="shrink-0 text-[var(--fl-text-2)] hover:text-[var(--fl-error)] disabled:opacity-40"
                    tooltip="Delete contribution"
                  >
                    <Trash2 size={14} />
                  </Button>
                )}
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  )
}
